class ShippingInfo extends HTMLElement {
  constructor() {
    super();
    this._hasRendered = false;
  }

  static get observedAttributes() {
    return ['express-days', 'express-msg', 'express-quantity-threshold', 'data-variants'];
  }

  connectedCallback() {
    // On first connect, get the current variant id and run shipping logic
    const location = this.getAttribute('data-location');
    const data_variants = this.getAttribute('data-variants');
    const isSearchanise = this.getAttribute('data-searchanise') === 'true';

    if (location == 'product-list' || isSearchanise) {
      if (data_variants && data_variants.indexOf('shipping_info_variants') !== -1) {
        try {
          const variants = JSON.parse(data_variants.replace('shipping_info_variants', '[]'));
          this.processVariantsSequentially(variants);
        } catch (e) {
          this.applyShippingResult('hidden', false);
        }
      } else if (data_variants && data_variants !== 'shipping_info_variants') {
        try {
          const variants = JSON.parse(data_variants); // Handle direct JSON data (for Searchanise)
          this.processVariantsSequentially(variants);
        } catch (error) {
          this.refreshShippingInfo();
        }
      }
    } else if (location == 'product-page') {
      this.initializeProductPage();
      
      document.addEventListener('variant:change', (evt) => {
        const variant = evt.detail.variant;
        if (!variant) {
          this.applyShippingResult('hidden', false);
          return;
        }

        // Use shipping_variants_data script (Liquid output) so we don't depend on global.js merge
        const allowExpressShipping = this._getAllowExpressShippingForVariantId(variant.id);
        const is_available = variant.available;

        let var_inventory_data = variant.brightperlInventory;
        if (var_inventory_data == null) {
          const shippingRow = this._getShippingDataForVariantId(variant.id);
          if (shippingRow) var_inventory_data = shippingRow.brightperl_inventory ?? null;
        }

        if (allowExpressShipping === false || allowExpressShipping === 'false') {
          this.applyShippingResult('hidden', false);
        } else if (allowExpressShipping === true || allowExpressShipping === 'true') {
          if (is_available) {
            this.applyShippingResult('express', true);
          } else {
            this.applyShippingResult('hidden', false);
          }
        } else {
          this.processShippingDetails(is_available, var_inventory_data);
        }

      });
    } else if (location == 'cart-page') {
      const available = this.getAttribute('data-item-available') === 'true';
      const inventoryMetafield = this.getAttribute('data-brightperl-inventory');
      const allowExpressShipping = this.getAttribute('data-allow-express-shipping');

      let inventoryData = null;
      if (available && inventoryMetafield && inventoryMetafield !== 'null') {
        try {
          inventoryData = (inventoryMetafield.trim().startsWith('[') || inventoryMetafield.trim().startsWith('{'))
            ? JSON.parse(inventoryMetafield)
            : inventoryMetafield;
        } catch (e) {
          inventoryData = inventoryMetafield;
        }
      }

      this._applyFromAllowExpress(available, allowExpressShipping, inventoryData);
    }
  }

  initializeProductPage() {
    // Try to get shipping variants data immediately
    const shippingVariantsScript = document.getElementById('shipping_variants_data');
    if (shippingVariantsScript && shippingVariantsScript.textContent.trim()) {
      try {
        const variants = JSON.parse(shippingVariantsScript.textContent);
        this.processVariantsSequentially(variants);
        return;
      } catch (e) {}
    }

    const variantsScript = document.getElementById('variants_data');
    if (variantsScript) {
      try {
        const variants = JSON.parse(variantsScript.textContent);
        this.processVariantsSequentially(variants);
        return;
      } catch (e) {}
    }

    this.refreshShippingInfo();
  }

  // Method to handle infinite loading - reset state for new variants
  resetForInfiniteLoading() {
    this.setAttribute('data-shipping-type', '');
  }

  /** type: 'express' | 'hidden'. skipRender = true only sets attribute. */
  applyShippingResult(type, renderArg = false, skipRender = false) {
    this.setAttribute('data-shipping-type', type);
    if (!skipRender) this.render(renderArg);
  }

  /** Get row from #shipping_variants_data for variant id (no dependency on global.js). */
  _getShippingDataForVariantId(variantId) {
    if (variantId == null) return null;
    try {
      const script = document.getElementById('shipping_variants_data');
      if (!script || !script.textContent) return null;

      const arr = JSON.parse(script.textContent);
      if (!Array.isArray(arr)) return null;

      return arr.find((v) => v && String(v.id) === String(variantId)) || null;
    } catch (e) {
      return null;
    }
  }

  /** Get allow_express_shipping for variant from #shipping_variants_data (for PDP variant:change). */
  _getAllowExpressShippingForVariantId(variantId) {
    const row = this._getShippingDataForVariantId(variantId);
    return row && Object.prototype.hasOwnProperty.call(row, 'allow_express_shipping') ? row.allow_express_shipping : undefined;
  }

  /** Apply badge from allow_express_shipping (false → hide; true+available → express; else Miramar logic). */
  _applyFromAllowExpress(available, allowExpressShipping, inventoryData) {
    if (allowExpressShipping === false || allowExpressShipping === 'false') {
      this.applyShippingResult('hidden', false);
    } else if (allowExpressShipping === true || allowExpressShipping === 'true') {
      this.applyShippingResult(available ? 'express' : 'hidden', !!available);
    } else {
      this.processShippingDetails(available, inventoryData);
    }
  }

  refreshShippingInfo() {
    if (this.getAttribute('data-location') !== 'product-page') return;
    const variantSelector = document.querySelector('.ctm_variant_all_listing');
    if (!variantSelector) return;

    const available = variantSelector.getAttribute('data-available') === 'true';
    const inventoryMetafield = variantSelector.getAttribute('data-brightperl-inventory');
    const allowExpressShipping = variantSelector.getAttribute('data-allow-express-shipping');

    let inventoryData = null;
    if (inventoryMetafield && inventoryMetafield !== 'null') {
      try {
        inventoryData = (inventoryMetafield.trim().startsWith('[') || inventoryMetafield.trim().startsWith('{'))
          ? JSON.parse(inventoryMetafield)
          : inventoryMetafield;
      } catch (e) {
        inventoryData = inventoryMetafield;
      }
    }
    this._applyFromAllowExpress(available, allowExpressShipping, inventoryData);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // Handle when new variants are loaded via infinite scrolling or metafield data is updated
    if (name === 'data-variants' && oldValue !== newValue && newValue) {
      const location = this.getAttribute('data-location');
      const isSearchanise = this.getAttribute('data-searchanise') === 'true';

      if (location === 'product-list' || isSearchanise) {
        try {
          this.resetForInfiniteLoading();
          if (newValue.indexOf('shipping_info_variants') < 0) {
            const storedData = JSON.parse(newValue);
            this.processVariantsSequentially(storedData);
          }
        } catch (error) {
          
        }
      }
    }
  }

  processShippingDetails(available, inventoryData, skipRender = false) {
    this.setAttribute('data-shipping-type', '');

    if (!available) {
      this.applyShippingResult('hidden', false, skipRender);
      return false;
    }

    this._updateShippingTypeFromInventory(inventoryData, available);
    if (!skipRender) this.render(available);

    return !!this.getAttribute('data-shipping-type');
  }

  async processVariantsSequentially(variants) {
    const location = this.getAttribute('data-location');
    const isSearchanise = this.getAttribute('data-searchanise') === 'true';
    this.setAttribute('data-shipping-type', '');

    if (!variants || variants.length === 0) {
      this.applyShippingResult('hidden', false);
      return;
    }

    if (location === 'product-list' || isSearchanise) {
      const variantLevel = this.getAttribute('data-variant-level') === 'true';

      // Variant-level: only on offer collections (e.g. 2-for-99) or when variant selector exists – use selected variant only
      if (variantLevel) {
        const selectedId = this.getAttribute('data-selected-variant-id');
        let currentVariant = null;
        
        if (selectedId && variants.length > 0) {
          currentVariant = variants.find((v) => String(v.id) === String(selectedId)) || variants.find((v) => String(v.variant_id) === String(selectedId));
          
          // Searchanise may send variant list without id; if selected variant id is set but no variant has id, use first variant (offer variant is often first)
          if (!currentVariant && variants.every((v) => v.id == null && v.variant_id == null)) {
            currentVariant = variants[0];
          }
        } else {
          currentVariant = variants[0];
        }

        if (!currentVariant) {
          this.applyShippingResult('hidden', false);
          return;
        }

        const { brightperlInventory, allowExpressShipping } = currentVariant;
        const available = currentVariant.available === true || currentVariant.available === '1' || currentVariant.available === 1;
        let inventoryData = null;
        if (brightperlInventory && brightperlInventory !== 'null' && brightperlInventory !== null) {
          inventoryData = brightperlInventory;
        }

        if (allowExpressShipping === false || allowExpressShipping === 'false') {
          this.applyShippingResult('hidden', false);
          return;
        }

        if (allowExpressShipping === true || allowExpressShipping === 'true') {
          if (available) {
            this.applyShippingResult('express', true);
          } else {
            this.applyShippingResult('hidden', false);
          }
          return;
        }

        this.processShippingDetails(available, inventoryData);
        return;
      }
      // Grid: only available variants; show if any has TRUE or (EMPTY + Miramar > threshold); hide if all FALSE
      const quantityThreshold = parseInt(this.getAttribute('express-quantity-threshold')) || 2;
      let showExpress = false;
      const anyAvailable = variants.some(
        (v) => v.available === true || v.available === '1' || v.available === 1
      );
      let allAvailableHaveExpressFalse = true;

      for (const variant of variants) {
        const { available, brightperlInventory, allowExpressShipping } = variant;
        const isAvailable = available === true || available === '1' || available === 1;

        if (isAvailable && allowExpressShipping !== false && allowExpressShipping !== 'false') {
          allAvailableHaveExpressFalse = false;
        }

        if (allowExpressShipping === false || allowExpressShipping === 'false') continue;
        if (!isAvailable) continue;

        if (allowExpressShipping === true || allowExpressShipping === 'true') {
          showExpress = true;
          break;
        }

        if (brightperlInventory && brightperlInventory !== 'null' && brightperlInventory !== null && typeof brightperlInventory === 'string') {
          const items = brightperlInventory.split(',').map((s) => s.trim());
          for (const item of items) {
            const parts = item.split('_');
            if (parts.length === 2 && parts[1] === 'Miramar') {
              const qty = parseInt(parts[0]) || 0;
              if (qty > quantityThreshold) {
                showExpress = true;
                break;
              }
            }
          }
        }
        if (showExpress) break;
      }

      if (allAvailableHaveExpressFalse) {
        this.applyShippingResult('hidden', false);
      } else if (showExpress) {
        this.applyShippingResult('express', anyAvailable);
      } else {
        this.applyShippingResult('hidden', false);
      }
      return;
    }

    // PDP: current variant only (TRUE+available, EMPTY+Miramar>threshold → show; FALSE → hide)
    const currentVariantElement = document.querySelector('.ctm_variant_all_listing');
    let currentVariant = null;

    const allow_express_shipping = currentVariantElement
      ? currentVariantElement.getAttribute('data-allow-express-shipping')
      : null;
    if (allow_express_shipping === 'false' || allow_express_shipping === false) {
      return this.applyShippingResult('hidden', false);
    }

    if (currentVariantElement) {
      // Get data directly from ctm_variant_all_listing
      const available = currentVariantElement.getAttribute('data-available') === 'true';
      const inventoryMetafield = currentVariantElement.getAttribute('data-brightperl-inventory');

      // Process the data directly from ctm_variant_all_listing
      let inventoryData = null;
      if (inventoryMetafield && inventoryMetafield !== 'null') {
        inventoryData = inventoryMetafield;
      }

      if (allow_express_shipping === true || allow_express_shipping === 'true') {
        this.applyShippingResult('express', available);
      } else {
        this.processShippingDetails(available, inventoryData);
      }
      
      return;
    }

    let currentVariantId = null;
    const variantSelectors = document.querySelectorAll('input[name="id"], select[name="id"]');
    for (const selector of variantSelectors) {
      if (selector.value) {
        currentVariantId = selector.value;
        break;
      }
    }

    // Find the current selected variant
    if (currentVariantId) {
      currentVariant = variants.find((variant) => variant.id == currentVariantId);
    }

    // If no current variant found, use the first available variant
    if (!currentVariant) {
      currentVariant = variants.find((variant) => variant.available);
    }

    // If still no variant found, use the first variant regardless of availability
    if (!currentVariant && variants.length > 0) {
      currentVariant = variants[0];
    }

    // Process the selected variant
    if (currentVariant) {
      const { available, brightperlInventory, allowExpressShipping } = currentVariant;

      // Parse inventory data from metafield
      let inventoryData = null;
      if (brightperlInventory && brightperlInventory !== 'null' && brightperlInventory !== null) {
        inventoryData = brightperlInventory;
      }

      if (allowExpressShipping === false || allowExpressShipping === 'false') {
        // Hide shipping tag completely when metafield is false
        this.applyShippingResult('hidden', false);
      } else if (allowExpressShipping === true || allowExpressShipping === 'true') {
        // When metafield is true, show express shipping if at least 1 inventory available
        this.applyShippingResult('express', available);
      } else {
        // When metafield is empty, use original logic (check Miramar + quantity)
        this.processShippingDetails(available, inventoryData);
      }
    } else {
      this.applyShippingResult('hidden', false);
    }
  }

  _updateShippingTypeFromInventory(data, available) {
    let shippingType = 'hidden';

    const quantityThreshold = parseInt(this.getAttribute('express-quantity-threshold')) || 2;

    if (data && data !== 'null' && data !== null && typeof data === 'string') {
      const inventoryItems = data.split(',').map((item) => item.trim());

      for (const item of inventoryItems) {
        const parts = item.split('_');
        if (parts.length === 2 && parts[1] === 'Miramar') {
          const qty = parseInt(parts[0]) || 0;
          if (qty > quantityThreshold) {
            shippingType = 'express';
            break;
          }
        }
      }
    }
    this.setAttribute('data-shipping-type', shippingType);
  }

  handleTagVisibility(containerSelector, selectors) {
    const container = this.closest(containerSelector);
    if (!container) return;

    const { priceBox, bestSeller, offerTag } = selectors;

    const priceBoxes = container.querySelectorAll(priceBox);
    const bestSellers = container.querySelectorAll(bestSeller);
    const offerTags = container.querySelectorAll(offerTag);

    if (offerTags.length > 0) {
      priceBoxes.forEach((el) => (el.style.display = 'none'));
      bestSellers.forEach((el) => (el.style.display = 'none'));
    } else if (priceBoxes.length > 0) {
      bestSellers.forEach((el) => (el.style.display = 'none'));
    }
  }

  _isListingBadgeLocation() {
    const location = this.getAttribute('data-location');
    const designStyle = this.getAttribute('design-style') || '';

    return (
      location === 'product-list' ||
      (location === 'product-page' && designStyle === 'product-list') ||
      (location === 'cart-page' && designStyle === 'product-list')
    );
  }

  _setListingBadgeHidden(isHidden) {
    if (!this._isListingBadgeLocation()) return;

    if (isHidden) {
      this.setAttribute('aria-hidden', 'true');
      return;
    }

    this.removeAttribute('aria-hidden');
  }

  render(available) {
    if (!available || available == `false`) {
      this.innerHTML = '';
      this._hasRendered = false;
      this.classList.add('hidden');
      this._setListingBadgeHidden(true);
      return;
    }

    const shippingType = this.getAttribute('data-shipping-type');
    if (shippingType !== 'express') {
      this.innerHTML = '';
      this._hasRendered = false;
      this.classList.add('hidden');
      this._setListingBadgeHidden(true);
      return;
    }

    this.classList.remove('hidden');
    this._setListingBadgeHidden(false);
    const expressDays = parseInt(this.getAttribute('express-days'), 10);
    let expressMsg = this.getAttribute('express-msg') || '';
    const location = this.getAttribute('data-location');
    const design_style = this.getAttribute('design-style') || '';
    const adaAriaLive = this.getAttribute('aria-live') || 'polite';

    let styles = '';
    const deliveryIconSvg = '<svg width="512" height="512" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M339.222 124H95.3107C90.3329 124 86.7773 127.556 86.7773 132.533V178.756H115.222C125.177 178.756 133 186.578 133 196.533C133 206.489 125.177 214.311 115.222 214.311H86.7773V220.711H146.511C156.466 220.711 164.288 228.533 164.288 238.489C164.288 248.444 156.466 256.267 146.511 256.267H86.7773V263.378H115.222C125.177 263.378 133 271.2 133 281.156C133 291.111 125.177 298.933 115.222 298.933H86.7773V330.933C86.7773 335.911 90.3329 339.467 95.3107 339.467H130.866C137.977 324.533 152.911 313.867 170.688 313.867C188.466 313.867 203.4 324.533 210.511 339.467H347.755V133.244C347.755 128.267 344.2 124 339.222 124ZM284.466 225.689L220.466 289.689C219.044 291.111 216.911 291.822 215.488 291.822C214.066 291.822 212.644 291.822 211.933 290.4C209.088 288.978 207.666 285.422 209.088 281.867L225.444 231.378L204.822 221.422C202.688 220.711 201.266 218.578 201.266 216.444C200.555 214.311 201.977 212.178 203.4 210.044L267.4 146.044C269.533 143.911 273.088 143.2 275.933 145.333C278.777 146.756 280.2 150.311 278.777 153.867L262.422 203.644L283.044 213.6C285.177 214.311 286.6 216.444 286.6 218.578C287.311 221.422 286.6 223.556 284.466 225.689Z" fill="currentColor"/><path d="M452.999 357.957C452.999 374.312 439.488 387.823 423.132 387.823C406.777 387.823 393.266 374.312 393.266 357.957C393.266 341.601 406.777 328.09 423.132 328.09C439.488 328.09 452.999 341.601 452.999 357.957Z" fill="currentColor"/><path d="M200.556 357.957C200.556 374.312 187.044 387.823 170.689 387.823C154.333 387.823 140.822 374.312 140.822 357.957C140.822 341.601 154.333 328.09 170.689 328.09C187.044 328.09 200.556 341.601 200.556 357.957Z" fill="currentColor"/><path d="M386.154 174.488H361.977V338.755H382.599C389.71 323.822 404.643 313.155 422.421 313.155C440.199 313.155 455.132 323.822 462.243 338.755H467.221C472.199 338.755 475.754 335.199 475.754 330.222V264.088C476.465 215.022 435.932 174.488 386.154 174.488ZM443.754 271.199C439.488 271.199 436.643 268.355 436.643 264.088C436.643 239.199 417.443 217.866 393.265 214.311C389.71 213.599 386.865 210.044 387.577 206.488C388.288 202.933 391.843 200.088 395.399 200.799C426.688 205.066 450.865 232.799 450.865 264.088C450.865 268.355 448.021 271.199 443.754 271.199Z" fill="currentColor"/><path d="M115.222 204.355H44.1111C39.8444 204.355 37 201.511 37 197.244C37 192.977 39.8444 190.133 44.1111 190.133H115.222C119.489 190.133 122.333 192.977 122.333 197.244C122.333 201.511 119.489 204.355 115.222 204.355Z" fill="currentColor"/><path d="M146.511 246.31H75.4002C71.1335 246.31 68.2891 243.466 68.2891 239.199C68.2891 234.932 71.1335 232.088 75.4002 232.088H146.511C150.778 232.088 153.622 234.932 153.622 239.199C153.622 243.466 150.778 246.31 146.511 246.31Z" fill="currentColor"/><path d="M115.222 288.265H44.1111C39.8444 288.265 37 285.421 37 281.154C37 276.887 39.8444 274.043 44.1111 274.043H115.222C119.489 274.043 122.333 276.887 122.333 281.154C122.333 285.421 119.489 288.265 115.222 288.265Z" fill="currentColor"/></svg>';
    let shippingIcon = deliveryIconSvg;

    if (location == 'product-page') {
      if (design_style != 'product-list') {
        styles = `
            .delivery-date-wrapper{
              background-color: #F7F7F7;
              padding: 5px 15px;
              border-radius: 6px;
              margin-block: 10px;
            }
            .delivery-msg-wrapper{
              margin: 10px 0px;
              font-size: 16px;
              line-height: 20px;
              color: #000;
            }
            .delivery-msg-wrapper{
              justify-content: space-between;
              margin-bottom: 0px;
              display: flex;
              align-items: flex-start;
              flex-wrap: wrap;
            }
            .express-delivery-msg,
            .eco-delivery-msg{
              width: 100%;
              margin-bottom: 10px;
            }
            .delivery-icon-text-wrapper{
              display: flex;
              gap: 10px;
              align-items: flex-start;
            }
            .delivery-icon-wrapper svg{
              width: 35px;
              height: 35px;
              fill: #744f34;
              color: #744f34;
            }
            .delivery-type{
              font-size: 14px;
              font-weight: 700;
              color: #000;
              margin-bottom: 2px;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
              text-align: left;
              color: #17120f;
              font-style: normal;
              line-height: 20px;
            }
            .delivery-text-wrapper{
              font-size: 14px;
              color: #000;
              font-weight: normal;
            }
            .delivery-text-wrapper span{
              font-weight: 600;
            }
            .delivery-text-wrapper .delivery-date-text{
              color: #582BDE; 
            }
            .delivery-estimate-date{
              font-size: 12px;
              color: #666;
              margin-top: 5px;
              margin-bottom: 2px;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
              text-align: left;
              color: #17120f;
              font-style: normal;
              line-height: 20px;
            }
            @media (max-width: 480px) {
              .delivery-msg-wrapper{
                flex-direction: column; 
                gap: 15px;
              }
              .express-delivery-msg,
              .eco-delivery-msg{ 
                width: 100%;
              }
            }
          `;
      }
    }

    // Show "2 Day Shipping" only when shippingType is 'express'
    let expressHtml = '';
    this.innerHTML = '';
    let expressDaysMsg = expressDays;

    if (expressMsg.indexOf('[days]') > -1) {
      expressDaysMsg = expressDays + ' ' + expressMsg.split('[days]')[1];
      expressMsg = expressMsg.split('[days]')[0];
    }

    if (shippingType === 'express') {
      if (location == 'product-page') {
        if (design_style == 'product-list') {
          expressHtml = `<div class="express-delivery-msg">
                          <div class="delivery-icon-wrapper" aria-hidden="false">${shippingIcon} Express <span class="vertical-line"></span><strong>${expressDays} Day Shipping</strong></div>
                        </div>`;
        } else {
          expressHtml = `<div class="express-delivery-msg">
                          <div class="delivery-icon-text-wrapper">
                            <div class="delivery-icon-wrapper" aria-hidden="false">${shippingIcon}</div>
                            <div class="delivery-text-wrapper">
                              <div class="delivery-type">Express Shipping</div>
                              <div class="delivery-estimate-date"> 
                                ${expressMsg} <span class="delivery-date-text">${expressDaysMsg}</span>
                              </div>
                            </div>
                          </div>
                        </div>`;
        }
      } else if (location == 'product-list') {
        this.handleTagVisibility.call(this, '.product-tags-wrap', {
          priceBox: '.price-box',
          bestSeller: '.bestseller-tag',
          offerTag: '.offer-tag-style:not(.bestseller-tag):not(.price-box)',
        });

        this.handleTagVisibility.call(this, '.inner-top', {
          priceBox: '.price-box .price-regular',
          bestSeller: '.bestseller-tag',
          offerTag: '.offer-tag-style:not(.bestseller-tag):not(.price-box)',
        });

        this.handleTagVisibility.call(this, '.product-bottom', {
          priceBox: '.price-box .price-regular .price-regular',
          bestSeller: '.best-sellers',
          offerTag: '.custom-discount-offer:not(.best-sellers)',
        });

        expressHtml = `<div class="express-delivery-msg">
                        <div class="delivery-icon-wrapper" aria-hidden="false">${shippingIcon} Express <span class="vertical-line"></span><strong>${expressDays} Day Shipping</strong></div>
                      </div>`;
      } else if (location == 'cart-page') {
        if (design_style == 'product-list') {
          expressHtml = `<div class="express-delivery-msg">
                          <div class="delivery-icon-wrapper" aria-hidden="false">${shippingIcon} Express <span class="vertical-line"></span><strong>${expressDays} Day Shipping</strong></div>
                        </div>`;
        } else {
          expressHtml = `<div class="express-delivery-msg"> 
                          <div class="delivery-estimate-date desktop-only">
                            Express Shipping - ${expressMsg} <strong>${expressDaysMsg}</strong>
                          </div>
                          <div class="delivery-estimate-date mobile-only">
                            Express Shipping <br> ${expressMsg} <strong>${expressDaysMsg}</strong>
                          </div>
                        </div>`;
        }
      }
    }
    if (expressHtml != '') {
      const isListingBadge =
        location === 'product-list' ||
        (location === 'product-page' && design_style === 'product-list') ||
        (location === 'cart-page' && design_style === 'product-list');

      const deliveryMsgWrapperAttrs = isListingBadge
        ? ''
        : ` aria-live="${adaAriaLive}"`;

      this.innerHTML = `<style>${styles}</style>
                        <div class="delivery-date-wrapper">
                          <div class="delivery-msg-wrapper"${deliveryMsgWrapperAttrs}>
                            ${expressHtml}
                          </div>
                        </div>`;
      this._hasRendered = true;
    } else {
      this._hasRendered = false;
      this.classList.add('hidden');
      this._setListingBadgeHidden(true);
    }
    return true;
  }
}
customElements.define('shipping-info', ShippingInfo);